import { useQuery } from '@tanstack/react-query';

import type { TimeChartData, TimeChartItemRaw } from 'ui/shared/chart/types';

import useFetch from 'lib/hooks/useFetch';

import prepareChartItems from '../indicators/utils/prepareChartItems';

export type NewbitKlinePeriod = '15MIN' | '1HOUR' | '4HOUR' | '1DAY' | '1WEEK';

interface NewbitKlineItem {
  time: number;
  open: string;
  close: string;
  low: string;
  hight: string;
  vol: string;
}

export interface NewbitKlineResponse {
  code: number;
  data: Array<NewbitKlineItem> | null;
}

const LIMITS: Record<NewbitKlinePeriod, number> = {
  '15MIN': 96,
  '1HOUR': 168,
  '4HOUR': 180,
  '1DAY': 90,
  '1WEEK': 104,
};

const REFETCH_INTERVAL = 60_000;

export function mapKlineToChartItems(response: NewbitKlineResponse): Array<TimeChartItemRaw> {
  return (response.data || []).map((item) => ({ date: new Date(item.time), value: Number(item.close) }));
}

export function useNewbitKlineRawQuery(period: NewbitKlinePeriod, enabled = true) {
  const fetch = useFetch();

  return useQuery({
    queryKey: [ 'newbit-kline', period ],
    queryFn: () => fetch<NewbitKlineResponse, unknown>('/node-api/newbit/kline', {
      method: 'POST',
      body: { period, limit: LIMITS[period] },
    }) as Promise<NewbitKlineResponse>,
    refetchInterval: REFETCH_INTERVAL,
    enabled,
  });
}

export default function useNewbitKlineQuery(period: NewbitKlinePeriod) {
  const query = useNewbitKlineRawQuery(period);

  const data: TimeChartData = [ {
    items: prepareChartItems(query.data ? mapKlineToChartItems(query.data) : []),
    name: 'NB/USDT',
    valueFormatter: (x: number) => x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 }) + ' USDT',
  } ];

  return {
    data,
    isError: query.isError,
    isPending: query.isPending,
  };
}
